<template>
  <aw-spinner />
</template>

<script setup lang="ts">
  import { computed, onMounted } from 'vue';
  import { navigateTo, useNuxtApp, useRoute } from 'nuxt/app';
  import { setErrorNotification } from '~~/common/utils/helper';
  import { M_GDPR } from '~~/common/config/Modal';
  import { PUSH_LOGIN_SUCCESS } from '~~/common/plugins/aw-analytics.js';
  import { useGetLocaleCodeByUrl } from '~~/common/composables/useLanguage';

  import { useModalStore } from '~~/common/stores/modal';
  import { useOrderStore } from '~~/common/stores/order';
  import { useAuthenticationStore } from '~~/common/stores/authentication';
  import { useUserStore } from '~~/common/stores/user';
  import { useTranslationStore } from '~~/common/stores/translation';
  import { useDeliveryStore } from '~~/shop/app/stores/delivery';
  import AwSpinner from '~~/common/components/Common/AwSpinner.vue';

  // eslint-disable-next-line no-undef
  definePageMeta({
    layout: 'default-without-modals',
  });

  const nuxtApp = useNuxtApp();
  const { $awAnalytics, $awSFMCClient, $localePath, $logger } = nuxtApp;
  const route = useRoute();
  const getLocaleCodeByUrl = useGetLocaleCodeByUrl();

  const authenticationStore = useAuthenticationStore();
  const orderStore = useOrderStore();
  const userStore = useUserStore();
  const modalStore = useModalStore();
  const deliveryStore = useDeliveryStore();
  const translationStore = useTranslationStore();

  const order = computed(() => orderStore.order);
  const isEcomUser = computed(() => userStore.isEcomUser);
  const isGdprOpened = computed(() => modalStore.activeGenericModal.type === M_GDPR);

  async function login () {
    try {
      await userStore.fetchUser();
      await deliveryStore.fetchMethod();
      $awAnalytics[PUSH_LOGIN_SUCCESS]();
      $awSFMCClient[PUSH_LOGIN_SUCCESS]();

      const rawState = Array.isArray(route.query.state) ? route.query.state[0] : route.query.state;
      const forwardUrl = rawState ?? undefined;
      const localeCode = getLocaleCodeByUrl(forwardUrl);
      await translationStore.fetchTranslations({ localeCode });

      if (!isGdprOpened.value && !userStore?.data?.personalInfo?.birthDate && !userStore?.data?.personalInfo?.isB2B) {
        modalStore.openProfileModal();
      } else if (!isGdprOpened.value && isEcomUser.value && order.value.mergeNeeded) {
        await orderStore.fetchCartMerge();
        modalStore.openCartMergeModal();
      }
      // Passed as a string, not as `{path}` — `path` is a pathname only, so a `?query` or `#hash`
      // carried by the return URL would be silently dropped.
      await navigateTo(forwardUrl);
    } catch (error) {
      $logger.error(error);
      await setErrorNotification({
        nuxtApp,
        error,
      });
      await navigateTo({ path: $localePath('/') });
    }
  }

  onMounted(async () => {
    try {
      await authenticationStore.loginWithSalesForce(route.query);
      await login();
    } catch (error) {
      $logger.error(error);
      await setErrorNotification({
        nuxtApp,
        error,
      });
      await navigateTo({ path: $localePath('/') });
    }
  });
</script>
